// MobileActionSheet — BottomSheet wrapper for wish tap on mobile (per
// `docs/spec/wishlist/DESIGN.md §3.5` + §5.3).
//
// Mobile tap on a wish card opens this sheet at the `large` detent
// (BottomSheet variant="mobile" = bottom-anchored, 90vh max — DESIGN §18.7:
// 「全部 data-detent="large"，不分 small/medium」). This comment used to say
// "medium detent" and `Wishes.tsx` said it twice more; there has only ever
// been one height, so the three claims described a detent that did not
// exist. The sheet offers primary action (view detail) + secondary actions
// (mark / edit / delete) — per the spec, only "view detail" navigates
// away; the secondary actions execute without leaving the sheet so
// the kanban context is preserved.
//
// Desktop path: tap navigates directly to /wishes/:id (per
// DESIGN §3.5 "Desktop 不受影响"). The Wishes.tsx route branches on
// useIsMobile() to pick the right handler.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Trash2, Edit3, type LucideIcon } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BottomSheet } from "@/components/patterns/BottomSheet";
import { HoldToConfirmButton } from "@/components/system/HoldToConfirmButton";
import { useToast } from "@/components/ui/toast";
import { WishStatusPickerSheet } from "@/components/wish/WishStatusPickerSheet";
import { cn } from "@/lib/utils/cn";
import { patchWish, deleteWish, type WishStatus, type WishSummary } from "@/lib/api/wishlist";
import { ApiError } from "@/lib/api/pair";
import { appErrorMessage } from "@/lib/i18n/app";
import { WISH_STATUS_LABEL } from "@/lib/i18n/wishlist";
import { restoreSentence, WISH_RESTORE_DAYS } from "@/lib/retentionWindows";
import { queryKeys } from "@/lib/queries/keys";

export interface MobileActionSheetProps {
  wish: WishSummary | null;
  onOpenChange: (open: boolean) => void;
  /**
   * W39.1 R1 (ADR-0080). Fires after a successful delete. The sheet closes at
   * that moment, so the undo bar cannot be rendered inside it — the board owns
   * it and anchors it where the card was.
   *
   * W49.0 P2-4: carries `restorable`, because `restore_wish` is author-only
   * while `delete_wish` is open to either partner. The board decides whether to
   * offer the affordance; it cannot work that out from the id alone.
   */
  onDeleted?: (payload: { id: string; name: string; restorable: boolean }) => void;
}

// W53.1 D-5: the three "next status" tables that used to live here are gone.
// Each offered **one** destination per status, so `completed → abandoned` was
// unreachable no matter what the server allowed — the UI could not express a
// free machine. `WishStatusPickerSheet` lists all four instead.

const PRIORITY_LABELS = { must: "必做", want: "想要", maybe: "也許" } as const;

export function MobileActionSheet({
  wish,
  onOpenChange,
  onDeleted,
  viewerId,
}: MobileActionSheetProps & {
  /** The signed-in user, for the restore-permission check. */ viewerId?: string | null;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const open = wish !== null;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  };

  const moveMutation = useMutation({
    mutationFn: async (status: WishStatus) => {
      if (!wish) throw new Error("no wish");
      return patchWish(wish.id, { status });
    },
    onSuccess: () => {
      invalidate();
    },
    // W53.1 D-5 (G-12). This had **no** `onError`, and the failure it was
    // hiding was the worst kind: the row fired `mutate` and then closed the
    // sheet, so a refused change left the card exactly where it was, the sheet
    // gone, and nothing anywhere saying why. Users re-tapped the same button.
    // The 409 that used to answer 「重新開啟」 was invisible by construction — the
    // server is fixed now, but this mutation can still fail (offline, 500), so
    // it has to speak either way.
    onError: (err) => {
      toast.push({
        message:
          err instanceof ApiError
            ? appErrorMessage(err.code, err.message)
            : "變更狀態失敗, 請稍候再試。",
        variant: "danger",
      });
    },
  });
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!wish) throw new Error("no wish");
      return deleteWish(wish.id);
    },
    onSuccess: () => {
      invalidate();
      // Read `wish` before closing: `onOpenChange(false)` makes the parent set
      // it to null, and the mutation's `onSuccess` runs after that.
      const deleted = wish
        ? {
            id: wish.id,
            name: wish.name,
            restorable: Boolean(viewerId) && wish.created_by === viewerId,
          }
        : null;
      onOpenChange(false);
      // The sheet is closing, so the bar has to be rendered by the board.
      // Hand the id + label up rather than rendering it here, where it would
      // disappear with the sheet.
      if (deleted) onDeleted?.(deleted);
    },
  });

  if (!wish) {
    // Render an inert sheet (still flagged with the data-testid) so
    // the Radix root stays mounted between sheet opens (otherwise the
    // close animation flickers).
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange} variant="mobile" title="心願">
        <div data-testid="mobile-action-sheet" />
      </BottomSheet>
    );
  }

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="mobile"
      title={wish.name}
      description={`${PRIORITY_LABELS[wish.priority]} · 類別 ${wish.category}`}
    >
      <div
        data-component="MobileActionSheet"
        data-testid="mobile-action-sheet"
        className="flex flex-col gap-s-2"
      >
        <ActionRow
          icon={ExternalLink}
          label="查看詳情"
          tone="primary"
          onClick={() => {
            onOpenChange(false);
            navigate(`/wishes/${wish.id}`);
          }}
        />
        {wish ? (
          <ActionRow
            icon={Edit3}
            label={`狀態 · ${WISH_STATUS_LABEL[wish.status]}`}
            tone="default"
            disabled={moveMutation.isPending}
            dataTestId="mobile-action-status"
            onClick={() => setStatusPickerOpen(true)}
          />
        ) : null}
        <ActionRow
          icon={Edit3}
          label="編輯"
          tone="default"
          // W49.0 §8. Was `disabled` with a hint reading 「W33.3 上線」 — a
          // promise with no route behind it. The five things a wish is made of
          // (name / category / priority / date / visibility) had no other entry
          // point: all five `patchWish` call sites changed `status` and nothing
          // else, so a typo in the name could not be corrected at all.
          onClick={() => {
            onOpenChange(false);
            navigate(`/wishes/${wish.id}/edit`);
          }}
        />
        <ActionRow
          icon={Trash2}
          label="刪除"
          tone="destructive"
          disabled={deleteMutation.isPending}
          // W39.1 R1 (ADR-0080): no confirm dialog. The old one said
          // 「此操作無法復原」 while the delete is a soft delete with a 7-day
          // restore window — the same lie the diary and anniversary dialogs
          // told. The sheet closes on success, so the undo bar cannot live
          // here; it is handed to the board via `onDeleted`.
          onClick={() => deleteMutation.mutate()}
          holdToConfirm
          hint={restoreSentence(WISH_RESTORE_DAYS)}
        />
      </div>

      {/* A second sheet rather than a popover stacked on this one — the same
          shape `WishPhotoPickerSheet` uses, and the reason is on 393×852: two
          dismissible layers over one screen is two ways out, not one. */}
      {wish ? (
        <WishStatusPickerSheet
          open={statusPickerOpen}
          onOpenChange={setStatusPickerOpen}
          current={wish.status}
          busy={moveMutation.isPending}
          onPick={(status) => moveMutation.mutate(status)}
        />
      ) : null}
    </BottomSheet>
  );
}

interface ActionRowProps {
  icon: LucideIcon;
  label: string;
  hint?: string;
  tone?: "primary" | "default" | "destructive";
  disabled?: boolean;
  onClick: () => void;
  /** W50.1 §1 — require a sustained press (destructive rows only). */
  holdToConfirm?: boolean;
  /** Overrides the label-derived testid; used by the status entry row. */
  dataTestId?: string;
}

function ActionRow({
  icon: Icon,
  label,
  hint,
  tone = "default",
  disabled,
  onClick,
  holdToConfirm = false,
  dataTestId,
}: ActionRowProps) {
  const shared = cn(
    // Apple HIG floor: 44pt touch target (per AGENTS.md §3 #14 + DESIGN §15)
    "min-h-12 w-full flex items-center gap-3 px-s-4 rounded-2xl text-body font-semibold transition-colors",
    "press",
    tone === "primary" && "bg-ink-1 text-paper hover:bg-ink-2",
    tone === "default" && "bg-paper-elevated text-ink-1 hover:bg-paper-calm",
    // W56.0: the destructive row wears `text-partner-strong`, the same colour
    // `patterns/ActionSheet` gives its `tone="danger"` row — one destructive
    // colour in the app, not two. The glyph inherits it (see below), exactly
    // as the patterns sheet's leading icon does.
    tone === "destructive" && "bg-paper-elevated text-partner-strong hover:bg-paper-calm",
    disabled && "opacity-50 cursor-not-allowed",
  );

  const glyph = (
    <Icon
      className={cn(
        "h-5 w-5 shrink-0",
        tone === "primary" && "text-paper",
        // The destructive glyph inherits the row's `text-partner-strong`
        // instead of painting its own — the patterns sheet's leading icon
        // does the same, so the two rows read identically.
      )}
      aria-hidden
    />
  );
  const trailing = (
    <>
      <span className="flex-1 text-left">{label}</span>
      {hint ? <span className="text-meta text-ink-3">{hint}</span> : null}
    </>
  );

  // W50.1 §1 — the destructive row needs a sustained press, like every other
  // delete entry point. Routed through `HoldToConfirmButton` rather than a
  // private timer so all four sites share one state machine and one ring.
  if (holdToConfirm) {
    return (
      <HoldToConfirmButton
        onConfirm={onClick}
        label={label}
        icon={glyph}
        disabled={disabled}
        className={shared}
        testId={`mobile-action-row-${label}`}
      >
        {trailing}
      </HoldToConfirmButton>
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-component="MobileActionSheetRow"
      data-testid={dataTestId ?? `mobile-action-row-${label}`}
      className={shared}
    >
      {glyph}
      {trailing}
    </button>
  );
}
